import { fileSize, prepareSample, type SampleUpload } from '../lib/samples';
import { useCustomCss } from '../lib/customCss';
import { BookOpen, CalendarDays, Check, ChevronDown, ChevronRight, Copy, Flag, GraduationCap, Hand, Laptop, LifeBuoy, LogOut, MessageCircle, Pencil, Send, Trash2 } from 'lucide-react';
import { StudentSupport, useStudentSupportUnread } from './StudentSupport';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, studentApi, tokens } from '../lib/api';
import type { Assignment, PlannerItem, ReadingEntry } from '../lib/types';
import { STICKERS } from '../lib/types';
import { gradePct } from '../lib/grades';
import { applyPalette } from '../lib/palette';
import { Logo } from '../ui/Logo';
import { ThemePicker } from '../ui/ThemePicker';
import { Sheet } from '../ui/Sheet';
import { Whiteboard } from '../parent/Whiteboard';
import type { Msg as BoardMsg } from '../lib/socket';
import { freshStartOnIos } from '../lib/freshStart';

interface StudentMe {
  id: string;
  name: string;
  grade: string;
  /** The student's own color theme (#251). */
  theme?: string;
  /** The parent turned off "Children can change their theme" (#819). */
  themeLocked?: boolean;
  color: string;
  /** What this student sees (#454); missing on older servers (everything). */
  features?: string[];
}
interface Msg {
  id: number;
  from: 'parent' | 'child';
  name: string;
  text: string;
  ts: number;
}

type Tab = 'planner' | 'grades' | 'reading' | 'chat' | 'whiteboard' | 'setup' | 'support';

/** The Student Portal (#136): a child signs in with a parent-set code + password to see their own work. */
export function StudentApp() {
  useCustomCss('student'); // an admin's custom styles (#367)
  const [me, setMe] = useState<StudentMe | null>(null);
  // Opened by the child's ClassGuardian app (#684): a one-time link after # signs them in. Read once, then dropped from the address.
  const [linkCode] = useState(() => {
    const m = /(?:^#|&)signin=([\w-]{20,100})/.exec(window.location.hash);
    if (m) window.history.replaceState(null, '', window.location.pathname + window.location.search);
    return m?.[1] ?? '';
  });
  const [linkError, setLinkError] = useState('');
  const [checking, setChecking] = useState(!!tokens.student() || !!linkCode);

  useEffect(() => {
    if (!linkCode) return;
    studentApi
      .post<{ token: string; child: StudentMe }>('/api/student/link-signin', { code: linkCode })
      .then((r) => {
        tokens.setStudent(r.token);
        setMe(r.child);
        applyPalette(r.child.theme);
      })
      .catch((e) => {
        // Still signed in here from before? Keep that; otherwise say why and show the sign-in form.
        setLinkError(e instanceof ApiError ? e.message : 'Something went wrong.');
        if (tokens.student()) return studentApi.get<{ child: StudentMe }>('/api/student/me').then((r) => setMe(r.child));
      })
      .catch(() => undefined)
      .finally(() => setChecking(false));
  }, [linkCode]);

  useEffect(() => {
    if (!tokens.student() || linkCode) return;
    studentApi
      .get<{ child: StudentMe }>('/api/student/me')
      .then((r) => {
        setMe(r.child);
        applyPalette(r.child.theme);
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) tokens.setStudent(null);
      })
      .finally(() => setChecking(false));
  }, [linkCode]);

  const signOut = useCallback(() => {
    studentApi.post('/api/student/logout').catch(() => undefined);
    tokens.setStudent(null);
    setMe(null);
  }, []);

  if (checking) {
    return (
      <main className="center-page">
        <Logo large />
      </main>
    );
  }
  // On iPhone/iPad, load fresh after signing in so the layout isn't left offset by the keyboard (#427).
  if (!me) return <StudentLogin notice={linkError} onSignedIn={(m) => freshStartOnIos('/student') || setMe(m)} />;
  return <StudentDashboard me={me} onSignOut={signOut} />;
}

function StudentLogin({ onSignedIn, notice = '' }: { onSignedIn: (m: StudentMe) => void; notice?: string }) {
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await studentApi.post<{ token: string; child: StudentMe }>('/api/student/login', { code, password });
      tokens.setStudent(r.token);
      onSignedIn(r.child);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="center-page">
      <form className="card stack student-login" onSubmit={submit}>
        <Logo large />
        <h1>Student sign-in</h1>
        {notice && <p className="form-error">{notice}</p>}
        <p className="muted small">Use the code and password your parent gave you, or open the Student Portal from ClassGuardian on your computer.</p>
        <label>
          Your code
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="ABCD-2345" autoCapitalize="characters" autoComplete="off" autoFocus />
        </label>
        <label>
          Password
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
        </label>
        {error && <p className="form-error">{error}</p>}
        <button className="btn primary" disabled={busy || !code.trim() || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </main>
  );
}

function StudentDashboard({ me, onSignOut }: { me: StudentMe; onSignOut: () => void }) {
  // What the parent shows for this student (#454): hidden tabs and buttons go away.
  const shows = (f: string) => !me.features || me.features.includes(f);
  const TAB_KEY: Partial<Record<Tab, string>> = { planner: 'planner', grades: 'grade_book', reading: 'reading_log', chat: 'chat', whiteboard: 'whiteboard' };
  const tabShown = (t: Tab) => !TAB_KEY[t] || shows(TAB_KEY[t]!);
  const [tab, setTab] = useState<Tab>(() => (['planner', 'grades', 'reading', 'chat', 'whiteboard', 'setup'] as Tab[]).find(tabShown) ?? 'setup');
  const supportUnread = useStudentSupportUnread(tab);
  const [theme, setTheme] = useState(me.theme ?? '');
  const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'planner', label: 'Planner', icon: <CalendarDays size={18} /> },
    { id: 'grades', label: 'Grades', icon: <GraduationCap size={18} /> },
    { id: 'reading', label: 'Reading log', icon: <BookOpen size={18} /> },
    { id: 'chat', label: 'Chat', icon: <MessageCircle size={18} /> },
    { id: 'whiteboard', label: 'Whiteboard', icon: <Pencil size={18} /> },
    { id: 'setup', label: 'Set up devices', icon: <Laptop size={18} /> },
    { id: 'support', label: 'Help & Feedback', icon: <LifeBuoy size={18} /> },
  ];
  return (
    <div className="student-shell">
      <header className="student-header">
        <Logo />
        <div className="grow" />
        <span className="student-who">
          {me.name}
          {me.grade ? ` · ${me.grade}` : ''}
        </span>
        {/* Hidden when the parent chose the theme (#819). */}
        {!me.themeLocked && (
        <ThemePicker
          compact
          value={theme}
          onChange={(t) => {
            setTheme(t);
            applyPalette(t);
            studentApi.put('/api/student/theme', { theme: t }).catch(() => undefined);
          }}
        />
        )}
        {/* Each can be hidden on its own (#625). */}
        {shows('help') && <HelpButton />}
        {shows('done') && <DoneButton />}
        <button className="btn small ghost" onClick={onSignOut} aria-label="Sign out">
          <LogOut size={15} /> <span className="btn-label">Sign out</span>
        </button>
      </header>
      <nav className="student-tabs" role="tablist">
        {TABS.filter((t) => tabShown(t.id)).map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'active' : ''} onClick={() => setTab(t.id)}>
            {t.icon}
            <span>{t.label}</span>
            {t.id === 'support' && supportUnread > 0 && <span className="nav-dot" aria-label="New reply" />}
          </button>
        ))}
      </nav>
      <main className="student-main">
        {tab === 'planner' && <StudentPlanner />}
        {tab === 'grades' && <StudentGrades />}
        {tab === 'reading' && <StudentReading />}
        {tab === 'chat' && <StudentChat me={me} />}
        {tab === 'whiteboard' && <StudentWhiteboard />}
        {tab === 'setup' && <StudentSetup me={me} />}
        {tab === 'support' && <StudentSupport />}
      </main>
    </div>
  );
}

/** The student's daily reading log (#189). Parent sign-off is shown but read-only here. */
function StudentReading() {
  const [entries, setEntries] = useState<ReadingEntry[] | null>(null);
  const [error, setError] = useState('');
  const today = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const [date, setDate] = useState(today);
  const [title, setTitle] = useState('');
  const [pages, setPages] = useState('');
  const [minutes, setMinutes] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    studentApi
      .get<{ entries: ReadingEntry[] }>('/api/student/reading')
      .then((r) => (setEntries(r.entries), setError('')))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not load.'));
  }, []);
  useEffect(load, [load]);

  async function add() {
    setBusy(true);
    setError('');
    try {
      await studentApi.post('/api/student/reading', { date, title: title.trim(), pages: Number(pages) || 0, minutes: Number(minutes) || 0 });
      setTitle('');
      setPages('');
      setMinutes('');
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }
  async function remove(e: ReadingEntry) {
    if (!confirm(`Delete “${e.title}” on ${e.date}?`)) return;
    await studentApi.del(`/api/student/reading/${e.id}`).catch(() => undefined);
    load();
  }

  const shortDate = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return (
    <div className="stack">
      <section className="card stack">
        <h2 className="student-section-title">Add today’s reading</h2>
        <div className="reading-form">
          <label>
            Date
            <input type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className="reading-form-title">
            Book title
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="The Hobbit" />
          </label>
          <label>
            Pages read
            <input type="number" inputMode="numeric" min={0} value={pages} onChange={(e) => setPages(e.target.value)} placeholder="0" />
          </label>
          <label>
            Minutes reading
            <input type="number" inputMode="numeric" min={0} value={minutes} onChange={(e) => setMinutes(e.target.value)} placeholder="0" />
          </label>
        </div>
        {error && <p className="error">{error}</p>}
        <button className="btn primary" disabled={busy || !title.trim() || !date} onClick={add}>
          Add to my log
        </button>
      </section>

      <section className="card stack">
        <h2 className="student-section-title">My reading log</h2>
        {!entries ? (
          <p className="muted">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="muted">Nothing logged yet.</p>
        ) : (
          <div className="report-table-wrap">
            <table className="report-table reading-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Book</th>
                  <th>Pages</th>
                  <th>Min</th>
                  <th title="Signed off by your parent">OK</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td>{shortDate(e.date)}</td>
                    <td className="reading-title">{e.title}</td>
                    <td>{e.pages}</td>
                    <td>{e.minutes}</td>
                    <td>
                      {/* Read-only for the student: only a parent can sign off. */}
                      <span className={`signoff ${e.signedOff ? 'on' : ''}`} title={e.signedOff ? 'Signed off by your parent' : 'Not signed off yet'}>
                        {e.signedOff ? <Check size={14} /> : null}
                      </span>
                    </td>
                    <td>
                      {!e.signedOff && (
                        <button className="icon-btn" onClick={() => remove(e)} aria-label={`Delete ${e.title}`}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

interface StudentDevice {
  id: string;
  name: string;
  kind: 'agent' | 'extension';
  platform: string;
  online: boolean;
  lastSeenAt: number;
}

/**
 * Set up devices (#185): which of the student's devices already have ClassGuardian, and the steps to add
 * one. Pairing still needs a Family code from the parent, so this never creates codes itself.
 */
function StudentSetup({ me }: { me: StudentMe }) {
  const { data, error, loading } = useFetch<{ devices: StudentDevice[]; setupUrl: string }>('/api/student/devices', (r) => r);
  const [copied, setCopied] = useState(false);
  if (loading) return <p className="muted center">Loading…</p>;
  if (error || !data) return <p className="error center">{error || 'Could not load.'}</p>;

  const lastSeen = (ts: number) => (ts ? new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'never');
  const kindLabel = (d: StudentDevice) => (d.kind === 'agent' ? 'ClassGuardian app' : 'Browser extension') + (d.platform ? ` · ${d.platform}` : '');
  async function copy() {
    try {
      await navigator.clipboard.writeText(data!.setupUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the link is still shown */
    }
  }

  return (
    <div className="stack">
      <section className="card stack">
        <h2 className="student-section-title">Your devices</h2>
        {data.devices.length === 0 ? (
          <p className="muted">ClassGuardian isn’t set up on any of your devices yet.</p>
        ) : (
          <ul className="student-devices">
            {data.devices.map((d) => (
              <li key={d.id}>
                <span className={`dot ${d.online ? 'on' : ''}`} aria-hidden />
                <span className="grow">
                  <strong>{d.name}</strong>
                  <span className="muted small block">{kindLabel(d)}</span>
                </span>
                <span className="muted small">{d.online ? 'Online' : `Last seen ${lastSeen(d.lastSeenAt)}`}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card stack">
        <h2 className="student-section-title">Set up a new device</h2>
        <ol className="install-steps">
          <li>
            On the computer you use for school, open this page in <strong>Chrome</strong> or <strong>Edge</strong>:
            <div className="student-setup-link">
              <code>{data.setupUrl}</code>
              <button className="btn small" onClick={copy}>
                <Copy size={14} /> {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </li>
          <li>
            Follow the steps on that page. On a <strong>Windows</strong> computer or a <strong>Mac</strong>, your parent can install the
            ClassGuardian app instead (they may need to approve the install); on a <strong>Chromebook</strong> it adds the browser extension.
          </li>
          <li>
            When it asks for a <strong>Family code</strong>, ask your parent. They get one from your <em>Devices</em> page in ClassGuardian —
            it only works for a few minutes.
          </li>
          <li>
            You’ll see the School Resources button during school time. That’s how you know it’s working, {me.name}.
          </li>
        </ol>
        <p className="muted small">
          ClassGuardian only watches during school time and never records your screen.
        </p>
      </section>
    </div>
  );
}

interface DoneStatus {
  enabled: boolean;
  schoolTime: boolean;
  doneForDay: boolean;
  canRequest: boolean;
  request: { status: 'pending' | 'approved' | 'declined' } | null;
}

/**
 * "I'm done for today" (#440): finished early, so ask the parent to end school time until tomorrow. Shown only during
 * school time (or once done), and only when the server allows it.
 */
function DoneButton() {
  const [status, setStatus] = useState<DoneStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(() => {
    studentApi.get<DoneStatus>('/api/student/done').then(setStatus).catch(() => undefined);
  }, []);
  useEffect(() => {
    load();
    const t = window.setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);
  if (!status?.enabled || (!status.schoolTime && !status.doneForDay)) return null;
  const pending = status.request?.status === 'pending';
  async function ask() {
    setBusy(true);
    setError('');
    try {
      await studentApi.post('/api/student/done');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not send that. Try again.');
    } finally {
      setBusy(false);
      load();
    }
  }
  return (
    <button
      className={`btn small ${status.doneForDay ? 'active' : 'ghost'}`}
      onClick={ask}
      disabled={busy || status.doneForDay || pending}
      title={error || (status.request?.status === 'declined' ? 'Your parent said keep going a little longer.' : undefined)}
      aria-label={status.doneForDay ? 'Done for today' : pending ? 'Waiting for your parent' : 'I’m done for today'}
    >
      <Flag size={15} /> <span className="btn-label">{status.doneForDay ? 'Done for today 🎉' : pending ? 'Asked your parent' : 'I’m done for today'}</span>
    </button>
  );
}

/** Ask the parent for help (#232): they get an alert and a push notification right away. */
function HelpButton() {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function send() {
    setBusy(true);
    setError('');
    try {
      await studentApi.post('/api/student/help', { note: note.trim() || undefined });
      setSent(true);
      setNote('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not send that. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button className="btn small help-btn" onClick={() => (setOpen(true), setSent(false), setError(''))} aria-label="Ask for help">
        <Hand size={15} /> <span className="btn-label">Help</span>
      </button>
      {open && (
        <Sheet title="Ask for help" onClose={() => setOpen(false)}>
          <div className="stack">
            {sent ? (
              <p>
                <strong>Your parent has been told.</strong> They’ll check in with you soon.
              </p>
            ) : (
              <>
                <p className="muted">Your parent gets a notification right away.</p>
                <label>
                  What do you need help with? <span className="muted small">(optional)</span>
                  <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="e.g. I’m stuck on question 4" />
                </label>
                <button className="btn primary" disabled={busy} onClick={send}>
                  <Hand size={16} /> Ask for help
                </button>
              </>
            )}
            {error && <p className="error">{error}</p>}
          </div>
        </Sheet>
      )}
    </>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- each caller picks its own shape from the response
function useFetch<T>(path: string, pick: (r: any) => T, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    setLoading(true);
    studentApi
      .get<unknown>(path)
      .then((r) => live && (setData(pick(r)), setError('')))
      .catch((e) => live && setError(e instanceof ApiError ? e.message : 'Could not load.'))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, error, loading };
}

function StudentPlanner() {
  const [items, setItems] = useState<PlannerItem[] | null>(null);
  const [error, setError] = useState('');
  const [turningIn, setTurningIn] = useState<PlannerItem | null>(null);
  const [requireLearned, setRequireLearned] = useState(false);
  const load = useCallback(() => {
    studentApi
      .get<{ items: PlannerItem[]; requireLearned?: boolean }>('/api/student/planner')
      .then((r) => (setItems(r.items), setRequireLearned(!!r.requireLearned), setError('')))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not load.'));
  }, []);
  useEffect(load, [load]);
  async function undo(item: PlannerItem) {
    try {
      await studentApi.post(`/api/student/planner/${item.id}/unsubmit`);
      load();
    } catch {
      /* stays as it was */
    }
  }
  if (error) return <p className="form-error">{error}</p>;
  if (!items) return <p className="muted">Loading…</p>;
  const row = (i: PlannerItem, showDue = false) => <PlannerRow item={i} showDue={showDue} onTurnIn={() => setTurningIn(i)} onUndo={() => undo(i)} />;
  if (!items.length) return <p className="muted center">Nothing in your planner yet.</p>;
  return (
    <div className="stack">
      {/* One day at a time (#693), like School Resources on the computer. */}
      <DayPlanner items={items} row={row} />
      {turningIn && (
        <TurnInSheet
          item={turningIn}
          requireLearned={requireLearned}
          onClose={() => setTurningIn(null)}
          onDone={() => {
            setTurningIn(null);
            load();
          }}
        />
      )}
    </div>
  );
}

/** Turn in an assignment (#188): what I learned today, a sticker, and it goes to the parent as done. */
function TurnInSheet({ item, requireLearned = false, onClose, onDone }: { item: PlannerItem; requireLearned?: boolean; onClose: () => void; onDone: () => void }) {
  const [note, setNote] = useState(item.studentNote ?? '');
  const [sticker, setSticker] = useState(item.sticker || '⭐');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Work samples (#603): photos or PDFs of the work, when the server allows it.
  const [policy, setPolicy] = useState<{ enabled: boolean; maxMb: number } | null>(null);
  const [files, setFiles] = useState<SampleUpload[]>([]);
  const [turnedIn, setTurnedIn] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  useEffect(() => {
    studentApi
      .get<{ enabled: boolean; maxMb: number }>('/api/student/samples')
      .then(setPolicy)
      .catch(() => setPolicy(null));
  }, []);
  async function pick(list: FileList | null) {
    if (!list?.length || !policy) return;
    setError('');
    const next = [...files];
    for (const f of [...list]) {
      if (next.length >= 5) {
        setError('You can add up to 5 files.');
        break;
      }
      try {
        next.push(await prepareSample(f, policy.maxMb));
      } catch (e) {
        setError((e as Error).message);
      }
    }
    setFiles(next);
    if (picker.current) picker.current.value = '';
  }
  // A Portfolio-type assignment (#637) asks for the finished work until something's attached.
  const askForWork = item.category === 'Portfolio' && !!policy?.enabled && !files.length && !turnedIn;
  async function submit() {
    setBusy(true);
    setError('');
    try {
      if (!turnedIn) {
        await studentApi.post(`/api/student/planner/${item.id}/submit`, { note: note.trim(), sticker });
        setTurnedIn(true);
      }
      if (files.length) {
        try {
          await studentApi.post(`/api/student/planner/${item.id}/samples`, { files: files.map(({ name, data }) => ({ name, data })) });
        } catch (e) {
          setError(`Turned in, but the files didn’t upload: ${e instanceof ApiError ? e.message : 'try again.'}`);
          return;
        }
      }
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not turn it in. Try again.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Sheet title={`Turn in: ${item.title}`} onClose={onClose}>
      <div className="stack">
        <label>
          <span>
            What I learned today {requireLearned && <span className="muted small">(required)</span>}
          </span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} maxLength={2000} placeholder="One or two things you learned…" />
        </label>
        <div>
          <div className="muted small">Pick a sticker</div>
          <div className="sticker-grid" role="radiogroup" aria-label="Sticker">
            {STICKERS.map((s) => (
              <button key={s} role="radio" aria-checked={sticker === s} className={`sticker${sticker === s ? ' active' : ''}`} onClick={() => setSticker(s)}>
                {s}
              </button>
            ))}
          </div>
        </div>
        {askForWork && (
          <div className="portfolio-work stack tight">
            <strong>This one goes in your portfolio.</strong>
            <span className="small">Add a photo of your finished work.</span>
          </div>
        )}
        {policy?.enabled && (
          <div className="stack">
            {files.map((f, i) => (
              <div key={`${f.name}-${i}`} className="row-between list-row">
                <span className="small">
                  {f.name} · {fileSize(f.size)}
                </span>
                <button type="button" className="icon-btn" aria-label={`Remove ${f.name}`} title="Remove" onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                  ×
                </button>
              </div>
            ))}
            {files.length < 5 && !turnedIn && !askForWork && (
              <button type="button" className="btn small" onClick={() => picker.current?.click()}>
                Add a photo or file
              </button>
            )}
            <input ref={picker} type="file" accept="image/*,application/pdf" multiple hidden onChange={(e) => void pick(e.target.files)} />
          </div>
        )}
        {error && <p className="error">{error}</p>}
        {turnedIn && error ? (
          <button className="btn primary" onClick={onDone}>
            Done
          </button>
        ) : askForWork ? (
          <div className="row-gap wrap">
            <button className="btn primary" onClick={() => picker.current?.click()}>
              📷 Add a photo
            </button>
            <button className="btn" disabled={busy || (requireLearned && !note.trim())} onClick={submit}>
              Turn in without one
            </button>
          </div>
        ) : (
          <button className="btn primary" disabled={busy || (requireLearned && !note.trim())} onClick={submit}>
            {item.submittedAt ? 'Update' : 'Turn it in'} {sticker}
          </button>
        )}
      </div>
    </Sheet>
  );
}

const clockOf = (hhmm: string) => new Date(`2000-01-01T${hhmm}:00`).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fromYmd = (s: string) => new Date(`${s}T12:00:00`);
const addDays = (s: string, n: number) => {
  const d = fromYmd(s);
  d.setDate(d.getDate() + n);
  return ymd(d);
};

/**
 * The planner one day at a time (#693): ‹ day › and 📅 at the top, opening on today, with the overdue and undated work
 * a tap away so nothing gets lost.
 */
function DayPlanner({ items, row }: { items: PlannerItem[]; row: (i: PlannerItem, showDue?: boolean) => React.ReactNode }) {
  const today = ymd(new Date());
  const [day, setDay] = useState(today);
  const [list, setList] = useState<'' | 'overdue' | 'undated'>('');
  const [month, setMonth] = useState<string | null>(null);
  const go = (d: string) => {
    setDay(d);
    setList('');
    setMonth(null);
  };
  const overdue = items.filter((i) => !i.done && !i.event && i.dueDate && i.dueDate < today);
  const undated = items.filter((i) => !i.done && !i.dueDate);
  const todays = items.filter((i) => i.dueDate === day).sort((a, b) => (a.startTime || '99').localeCompare(b.startTime || '99'));
  const d = fromYmd(day);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const name = day === today ? 'Today' : day === addDays(today, 1) ? 'Tomorrow' : day === addDays(today, -1) ? 'Yesterday' : d.toLocaleDateString(undefined, { weekday: 'long' });
  const named = name === 'Today' || name === 'Tomorrow' || name === 'Yesterday';
  const dateLine = d.toLocaleDateString(undefined, { ...(named ? { weekday: 'long' as const } : {}), month: 'long', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' as const }) });
  return (
    <div className="stack">
      <div className="day-planner-head">
        <button type="button" className="round-btn" aria-label="Previous day" onClick={() => go(addDays(day, -1))}>
          ‹
        </button>
        <div className="grow center">
          <h2>{list === 'overdue' ? 'Overdue' : list === 'undated' ? 'No due date' : name}</h2>
          {!list && <div className="muted small">{dateLine}</div>}
        </div>
        <button type="button" className="round-btn" aria-label="Next day" onClick={() => go(addDays(day, 1))}>
          ›
        </button>
        <button type="button" className="round-btn" aria-label="Pick a date" aria-expanded={!!month} onClick={() => setMonth(month ? null : day.slice(0, 8) + '01')}>
          📅
        </button>
      </div>
      {month && <MonthPicker month={month} chosen={day} withWork={new Set(items.map((i) => i.dueDate).filter(Boolean))} pick={go} turn={setMonth} />}
      {list ? (
        <>
          <button type="button" className="btn small" style={{ alignSelf: 'flex-start' }} onClick={() => setList('')}>
            ‹ Back to {day === today ? 'today' : d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
          </button>
          {(list === 'overdue' ? overdue : undated).map((i) => (
            <div key={i.id}>{row(i, true)}</div>
          ))}
        </>
      ) : (
        <>
          {day !== today && (
            <button type="button" className="btn small" style={{ alignSelf: 'center' }} onClick={() => go(today)}>
              Back to today
            </button>
          )}
          {day === today && overdue.length > 0 && (
            <button type="button" className="overdue-line" onClick={() => setList('overdue')}>
              ⚠ {overdue.length} overdue from earlier days ›
            </button>
          )}
          {todays.length === 0 && (
            <p className="muted center">
              Nothing planned for {day === today ? 'today' : d.toLocaleDateString(undefined, { weekday: 'long' })}. Use the arrows or 📅 to look at another day.
            </p>
          )}
          {todays.map((i) => (
            <div key={i.id}>{row(i)}</div>
          ))}
          {day === today && undated.length > 0 && (
            <button type="button" className="link-btn small" style={{ alignSelf: 'center' }} onClick={() => setList('undated')}>
              {undated.length} with no due date ›
            </button>
          )}
        </>
      )}
    </div>
  );
}

/** A month to pick a day from (#693): days with work underlined, the chosen day filled, today ringed. */
function MonthPicker({ month, chosen, withWork, pick, turn }: { month: string; chosen: string; withWork: Set<string>; pick: (d: string) => void; turn: (m: string) => void }) {
  const first = fromYmd(month);
  const shift = (n: number) => {
    const m = new Date(first);
    m.setMonth(m.getMonth() + n, 1);
    turn(ymd(m));
  };
  const days: string[] = [];
  for (let x = new Date(first); x.getMonth() === first.getMonth(); x.setDate(x.getDate() + 1)) days.push(ymd(x));
  const today = ymd(new Date());
  const names = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  return (
    <div className="card month-picker">
      <div className="row-between">
        <button type="button" className="round-btn small" aria-label="Previous month" onClick={() => shift(-1)}>
          ‹
        </button>
        <strong>{first.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</strong>
        <button type="button" className="round-btn small" aria-label="Next month" onClick={() => shift(1)}>
          ›
        </button>
      </div>
      <div className="month-grid">
        {names.map((n, k) => (
          <span key={k} className="muted small">
            {n}
          </span>
        ))}
        {Array.from({ length: first.getDay() }, (_, k) => (
          <span key={`lead${k}`} />
        ))}
        {days.map((dd) => (
          <button
            key={dd}
            type="button"
            className={`month-day${dd === chosen ? ' chosen' : ''}${dd === today ? ' today' : ''}${withWork.has(dd) ? ' work' : ''}`}
            aria-label={fromYmd(dd).toLocaleDateString(undefined, { dateStyle: 'full' }) + (withWork.has(dd) ? ', has work' : '')}
            onClick={() => pick(dd)}
          >
            {Number(dd.slice(8))}
          </button>
        ))}
      </div>
      <span className="muted small">Underlined days have work on them.</span>
    </div>
  );
}

function PlannerRow({ item, onTurnIn, onUndo, showDue = false }: { item: PlannerItem; onTurnIn: () => void; onUndo: () => void; showDue?: boolean }) {
  return (
    <div className={`card student-planner-item ${item.done ? 'done' : ''}`}>
      {item.sticker && (
        <span className="sticker-badge" aria-label="Your sticker">
          {item.sticker}
        </span>
      )}
      <div className="grow">
        <div className="student-planner-title">
          {item.done ? '✓ ' : ''}
          {item.url ? (
            <a href={item.url} target="_blank" rel="noreferrer">
              {item.title}
            </a>
          ) : (
            item.title
          )}
        </div>
        {item.description && <div className="muted small">{item.description}</div>}
        <div className="muted small">
          {item.subject && <span className="student-tag">{item.subject}</span>}
          {/* The day is in the header (#693); only the overdue and undated lists name each item's day. */}
          {showDue && item.dueDate && <span> Due {fromYmd(item.dueDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</span>}
          {item.startTime && <span> {clockOf(item.startTime)}{item.endTime ? `–${clockOf(item.endTime)}` : ''}</span>}
          {item.event && <span> · Class</span>}
        </div>
        {item.submittedAt && item.studentNote && <div className="small student-learned">I learned: {item.studentNote}</div>}
        <div className="row-gap student-turnin">
          {item.submittedAt ? (
            <>
              <span className="good-text small">Turned in</span>
              <button className="btn small ghost" onClick={onTurnIn}>
                Edit
              </button>
              <button className="btn small ghost" onClick={onUndo}>
                Undo
              </button>
            </>
          ) : item.event ? null : (
            /* A LiveLesson is attended, not turned in (#693). */
            <button className="btn small primary" onClick={onTurnIn}>
              Turn in
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** A subject's grade, the same sums as the parent's Grades tab (#821). */
const subjectAverage = gradePct;

function StudentGrades() {
  const { data, error, loading } = useFetch<Assignment[]>('/api/student/grades', (r) => r.assignments);
  const bySubject = useMemo(() => {
    const map = new Map<string, Assignment[]>();
    for (const a of data ?? []) {
      const key = a.subject || 'Other';
      (map.get(key) ?? map.set(key, []).get(key)!).push(a);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [data]);
  // Which subjects are open (#685), remembered in this browser, as in the parent's Grade Book (#665).
  const OPEN_KEY = 'cg.student.grades.open';
  const [open, setOpen] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(OPEN_KEY) || '[]') as string[]);
    } catch {
      return new Set();
    }
  });
  const setOpenSubjects = (list: string[]) => {
    const next = new Set(list);
    setOpen(next);
    try {
      localStorage.setItem(OPEN_KEY, JSON.stringify([...next]));
    } catch {
      /* storage unavailable */
    }
  };
  const toggle = (subject: string) => setOpenSubjects(open.has(subject) ? [...open].filter((x) => x !== subject) : [...open, subject]);
  const anchor = (subject: string) => `sg-${subject.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const showSubject = (subject: string) => {
    if (!open.has(subject)) setOpenSubjects([...open, subject]);
    requestAnimationFrame(() => document.getElementById(anchor(subject))?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };
  if (loading) return <p className="muted">Loading…</p>;
  if (error) return <p className="form-error">{error}</p>;
  if (!bySubject.length) return <p className="muted center">No grades yet.</p>;
  const many = bySubject.length > 1;
  return (
    <div className="stack">
      {/* At a glance: every subject's grade; tapping one opens it below. */}
      {many && (
        <div className="grade-tiles">
          {bySubject.map(([subject, items]) => {
            const avg = subjectAverage(items);
            return (
              <button key={subject} type="button" className={`grade-tile${open.has(subject) ? ' open' : ''}`} onClick={() => showSubject(subject)}>
                <span className="grade-tile-name">{subject}</span>
                <span className="grade-tile-pct">{avg !== null ? `${Math.round(avg)}%` : '—'}</span>
                <span className="muted small">{items.length} graded</span>
              </button>
            );
          })}
        </div>
      )}
      {many && (
        <div className="row-between">
          <strong>By subject</strong>
          <span className="row-gap">
            <button type="button" className="link-btn small" onClick={() => setOpenSubjects(bySubject.map(([s]) => s))}>
              Open all
            </button>
            <button type="button" className="link-btn small" onClick={() => setOpenSubjects([])}>
              Close all
            </button>
          </span>
        </div>
      )}
      {bySubject.map(([subject, items]) => {
        const avg = subjectAverage(items);
        // One subject on its own stays open; with several, each starts closed to its name and grade.
        const isOpen = !many || open.has(subject);
        return (
          <section key={subject} id={anchor(subject)} className="card stack">
            <button type="button" className="grade-subject row-between student-grade-head" onClick={() => toggle(subject)} aria-expanded={isOpen}>
              <span className="row-gap">
                {many && (isOpen ? <ChevronDown size={18} aria-hidden /> : <ChevronRight size={18} aria-hidden />)}
                <h2>{subject}</h2>
                <span className="muted small">· {items.length} graded</span>
              </span>
              {avg !== null && <span className="student-grade-avg">{Math.round(avg)}%</span>}
            </button>
            {isOpen && items.map((a) => {
              const pct = a.pointsAvailable > 0 ? Math.round((a.pointsEarned / a.pointsAvailable) * 100) : null;
              return (
                <div key={a.id} className="student-grade-row">
                  <span className="grow">{a.name}</span>
                  <span className="muted small">
                    {a.pointsAvailable > 0 ? `${a.pointsEarned}/${a.pointsAvailable}${pct !== null ? ` · ${pct}%` : ''}` : 'Not scored yet'}
                  </span>
                </div>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}

function StudentChat({ me }: { me: StudentMe }) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const logRef = useRef<HTMLDivElement>(null);

  const load = useCallback(() => {
    studentApi
      .get<{ messages: Msg[] }>('/api/student/chat')
      .then((r) => setMessages(r.messages))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000); // near-real-time; the parent sees your messages instantly
    return () => clearInterval(t);
  }, [load]);

  // Scroll only the message list to the newest message, never the page (#853).
  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [messages]);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setText('');
    setError('');
    try {
      const r = await studentApi.post<{ message: Msg }>('/api/student/chat', { text: t });
      setMessages((m) => [...m, r.message]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send.');
      setText(t);
    }
  }

  return (
    <div className="student-chat">
      <div ref={logRef} className="student-chat-log">
        {messages.length === 0 && <p className="muted center">Say hi to your parent 👋</p>}
        {messages.map((m) => (
          <div key={m.id} className={`student-bubble ${m.from === 'child' ? 'mine' : 'theirs'}`}>
            <div className="student-bubble-name">{m.from === 'child' ? 'You' : m.name}</div>
            <div>{m.text}</div>
          </div>
        ))}
      </div>
      {error && <p className="form-error">{error}</p>}
      <form className="student-chat-input" onSubmit={send}>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder={`Message from ${me.name}…`} maxLength={500} />
        <button className="btn primary" disabled={!text.trim()}>
          <Send size={16} />
        </button>
      </form>
    </div>
  );
}

/**
 * The shared whiteboard (#751): the same board as School Resources on the student's computer and the parent's. The
 * portal has no live connection, so it asks the server what's new every second while the tab is open (every few seconds
 * when the page is hidden) and posts each stroke it draws.
 */
function StudentWhiteboard() {
  const listeners = useRef(new Set<(m: BoardMsg) => void>());
  const seen = useRef(0);
  const mine = useRef(new Set<number>());
  const [off, setOff] = useState(false);

  useEffect(() => {
    let stopped = false;
    let timer = 0;
    const emit = (m: Record<string, unknown>) => listeners.current.forEach((fn) => fn({ type: 'whiteboard', ...m }));
    const tick = async () => {
      try {
        const r = await studentApi.get<{ seq: number; reset: boolean; strokes: { seq: number; stroke: unknown }[] }>(`/api/student/whiteboard?since=${seen.current}`);
        if (stopped) return;
        if (r.reset) emit({ clear: true });
        // Skip strokes drawn here (already on the canvas), unless the board is being redrawn from scratch.
        for (const s of r.strokes) if (r.reset || !mine.current.has(s.seq)) emit({ stroke: s.stroke });
        seen.current = r.seq;
        setOff(false);
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) setOff(true);
      }
      if (!stopped) timer = window.setTimeout(() => void tick(), document.hidden ? 5000 : 1000);
    };
    void tick();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, []);

  const send = useCallback((payload: object) => {
    void studentApi
      .post<{ seq: number }>('/api/student/whiteboard', payload)
      .then((r) => mine.current.add(r.seq))
      .catch(() => undefined);
  }, []);
  const subscribe = useCallback((fn: (m: BoardMsg) => void) => {
    listeners.current.add(fn);
    return () => {
      listeners.current.delete(fn);
    };
  }, []);

  if (off) return <p className="muted">The whiteboard isn’t turned on for you.</p>;
  return (
    <div className="student-whiteboard">
      <Whiteboard send={send} subscribe={subscribe} label="Your parent sees the same board, and so does School Resources on your computer." />
    </div>
  );
}
